import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Chip, Skeleton, Tooltip, Typography } from '@mui/material';
import { EmailIcon, FileAddIcon } from '@sharedCR/coreDesignSystem/icons';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';

import { selectActivityById, selectContactsWithBorrowers } from 'store/loanApplications/selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';

import { EmailStatus } from '../../../Activities.constants';
import { formatActivityDateTime, getRecipientsWithNames } from '../../../Activities.utils';
import HighlightText from '../../../HighlightText';

const EmailHeader = ({
  emailId,
  isCollapsed = true,
  isNew = false,
  emailsCount = 0,
  onEmailThreadClick = null,
  isBodyLoading,
}) => {
  const email = useSelector(selectActivityById(emailId));

  const isLightWeightEmailBodyAllowed = useFeatureToggle(FeatureFlag.EnableLightWeightEmailBody);

  const {
    activityDateTime,
    subject,
    preview,
    bodyString,
    from,
    to,
    cc,
    bcc,
    events = [],
    attachmentNames = [],
  } = email;

  const highlightText = useSelector(getDebouncedSearchValue);

  const contacts = useSelector(selectContactsWithBorrowers);

  const { personFullString = '', personString = '' } = useMemo(() => {
    const omitEmailsRegex = new RegExp(/ <[^>]*>/g);

    return events.reduce((acc, { type }) => {
      if (type === EmailStatus.SENT) {
        const recipients = [...to, ...cc, ...bcc];

        acc.personFullString = getRecipientsWithNames({
          contacts,
          receivers: recipients,
          separator: '\n',
        });
        acc.personString = `to ${getRecipientsWithNames({
          contacts,
          receivers: [recipients[0]],
        }).replaceAll(omitEmailsRegex, '')}`;

        if (recipients.length > 1) {
          acc.personString += ', ...';
        }
      }

      if (type === EmailStatus.RECEIVED) {
        acc.personFullString = getRecipientsWithNames({
          contacts,
          receivers: [from],
          separator: '\n',
        });
        acc.personString = `from ${getRecipientsWithNames({
          contacts,
          receivers: [from],
        }).replaceAll(omitEmailsRegex, '')}`;
      }

      return acc;
    }, {});
  }, [contacts, events, from, to, cc, bcc]);

  const dateTime = formatActivityDateTime(activityDateTime);
  const attachmentsCount = attachmentNames.length;

  return (
    <div
      className="w-full flex items-center px-20 py-12 cursor-pointer"
      onClick={onEmailThreadClick}
    >
      <EmailIcon className={clsx(isNew && 'text-green', 'mr-20')} size={36} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Typography noWrap={isCollapsed} title={subject} variant={isNew ? 'bold16' : 'regular16'}>
          {!!emailsCount && <Chip className="mr-8" label={emailsCount} size="small" />}
          <HighlightText highlight={highlightText} text={subject} />
        </Typography>
        {isCollapsed && (
          <div className="flex justify-between items-center">
            {isLightWeightEmailBodyAllowed ? (
              <Typography noWrap className="italic" color="textSecondary" variant="regular14">
                <HighlightText highlight={highlightText} text={preview || ''} />
              </Typography>
            ) : (
              isBodyLoading && (
                <Skeleton animation="pulse" className="h-20" variant="text" width="100%" />
              )
            )}
            {!isBodyLoading && (
              <Typography noWrap className="italic" color="textSecondary" variant="regular14">
                <HighlightText highlight={highlightText} text={bodyString || ''} />
              </Typography>
            )}

            {!!attachmentsCount && (
              <Chip
                className="ml-8"
                label={
                  <span className="flex items-center">
                    <FileAddIcon className="mr-4" size={IconSize[14]} />
                    {attachmentsCount} file(s)
                  </span>
                }
                size="small"
              />
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col items-end ml-20">
        {isCollapsed && (
          <Tooltip title={<span className="whitespace-pre-wrap">{personFullString}</span>}>
            <Typography noWrap color="textSecondary" variant="regular14">
              <HighlightText highlight={highlightText} text={personString} />
            </Typography>
          </Tooltip>
        )}
        <Typography variant="regular14">{dateTime}</Typography>
      </div>
    </div>
  );
};

EmailHeader.propTypes = {
  emailId: PropTypes.string.isRequired,
  isBodyLoading: PropTypes.bool.isRequired,
  isCollapsed: PropTypes.bool,
  isNew: PropTypes.bool,
  emailsCount: PropTypes.number,
  onEmailThreadClick: PropTypes.func,
};

export default memo(EmailHeader);
